import { Observable, map } from "rxjs";
import { marker } from "@jsverse/transloco-keys-manager/marker";
import { HttpClient } from "@angular/common/http";
import { environment } from "src/environments/environment";
import { PaginatedResult } from "src/app/support/paginated-result";
import { Injectable, inject } from "@angular/core";
import { UserNotify } from "src/app/support/notify-decorator";
import { PageRequest } from "src/app/support/page-request";
import { Role, RoleLight } from "../models/role/role";
import { RoleCreate } from "../models/role/role.create";
import { RoleDelete } from "../models/role/role.delete";
import { RoleLoad } from "../models/role/role.load";
import { RoleUpdate } from "../models/role/role.update";
import { sntTranslate } from "src/app/support/translate";
import { omitUndefined } from "src/app/support/object";

@Injectable()
export class RoleBackend {
  private readonly http = inject(HttpClient);

  load(request: RoleLoad): Observable<Role> {
    return this.http
      .get<Role>(`${environment.snoots.api.iam}/roles/${request.id}`)
      .pipe(map(translateRole));
  }

  loadLight(request: RoleLoad): Observable<RoleLight> {
    return this.http
      .get<RoleLight>(`${environment.snoots.api.iam}/roles/${request.id}`)
      .pipe(map(translateRole));
  }

  list(
    request: PageRequest,
    type: string[]
  ): Observable<PaginatedResult<Role>> {
    return this.http
      .get<PaginatedResult<Role>>(`${environment.snoots.api.iam}/roles`, {
        params: omitUndefined({
          p: request.page,
          s: request.pageSize,
          type: type.join(","),
          ...request.filterValues
        })
      })
      .pipe(
        map((response) => {
          return {
            ...response,
            docs: response.docs.map(translateRole)
          };
        })
      );
  }

  listAllLight({ type }: { type: string[] }): Observable<RoleLight[]> {
    return this.listLight({ page: 1, pageSize: 10000 }, type).pipe(
      map((response) => response.docs)
    );
  }

  listLight(
    request: PageRequest,
    type: string[]
  ): Observable<PaginatedResult<RoleLight>> {
    return this.http
      .get<PaginatedResult<RoleLight>>(`${environment.snoots.api.iam}/roles`, {
        params: omitUndefined({
          p: request.page,
          s: request.pageSize,
          type: type.join(","),
          ...request.filterValues,
          light: true
        })
      })
      .pipe(
        map((response) => {
          return {
            ...response,
            docs: response.docs.map(translateRole)
          };
        })
      );
  }

  @UserNotify({
    successMessage: marker("Ruolo creato"),
    errorMessage: marker("Impossibile creare il ruolo")
  })
  create(request: RoleCreate): Observable<Role> {
    return this.http.post<Role>(`${environment.snoots.api.iam}/roles`, request);
  }

  @UserNotify({
    successMessage: marker("Ruolo aggiornato"),
    errorMessage: marker("Impossibile aggiornare il ruolo")
  })
  update(roleId: string, request: RoleUpdate): Observable<string> {
    return this.http.patch<string>(
      `${environment.snoots.api.iam}/roles/${roleId}`,
      {
        name: request.name,
        description: request.description,
        icon: request.icon,
        rules: request.rules
      }
    );
  }

  @UserNotify({
    successMessage: marker("Ruolo rimosso"),
    errorMessage: marker("Impossibile rimuovere il ruolo")
  })
  delete(request: RoleDelete): Observable<string> {
    return this.http.delete<string>(
      `${environment.snoots.api.iam}/roles/${request.id}`
    );
  }
}

function translateRole<T extends RoleLight>(role: T): T {
  role.name = sntTranslate(role.name);
  return role;
}
